import * as React from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { RenderCard } from '@/components/RenderCard';
import { useSession } from '@/lib/session';
import { useRenderList } from '@/hooks/useRenderJob';
import { signedRenderUrl } from '@/lib/api';

export const RenderListPage = () => {
  const uid = useSession((s) => s.session?.user.id);
  const list = useRenderList(uid);

  // Pre-sign thumbnails in parallel (short TTL). Only for succeeded jobs.
  const thumbs = useQueries({
    queries: (list.data ?? [])
      .filter((j) => j.output_key)
      .map((j) => ({
        queryKey: ['thumb', j.id],
        queryFn: () => signedRenderUrl(j.output_key as string),
        staleTime: 50 * 60 * 1000,
      })),
  });
  const thumbByJobId = React.useMemo(() => {
    const map = new Map<string, string>();
    (list.data ?? [])
      .filter((j) => j.output_key)
      .forEach((j, i) => {
        const url = thumbs[i]?.data;
        if (typeof url === 'string') map.set(j.id, url);
      });
    return map;
  }, [list.data, thumbs]);

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6 lg:p-10">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display">Meus renders</h1>
          <p className="text-muted-foreground text-sm">{list.data?.length ?? 0} renders</p>
        </div>
        <Button asChild>
          <Link to="/upload">Novo render</Link>
        </Button>
      </header>

      {list.isLoading ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[4/3] rounded-lg bg-shimmer animate-shimmer" />
          ))}
        </div>
      ) : (list.data?.length ?? 0) === 0 ? (
        <div className="grid place-items-center rounded-lg border border-dashed p-12 text-center">
          <p className="mb-4 text-muted-foreground">Você ainda não tem renders.</p>
          <Button asChild>
            <Link to="/upload">Fazer primeiro render</Link>
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {list.data?.map((job) => (
            <RenderCard key={job.id} job={job} thumbUrl={thumbByJobId.get(job.id)} />
          ))}
        </div>
      )}
    </div>
  );
};
